// frontend/src/components/WhatsAppTemplateCreatorModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-12-16
// Updated:      2026-09-16
//
// Description:  Centre-screen modal for creating WhatsApp templates backed by Twilio
//               Content API. Stores template metadata and a variables_schema in the
//               whatsapp_templates table via /messaging/templates/whatsapp.
//
//               Layout requirements (2025-12-17):
//               • Wider modal, minimal scrolling
//               • Single-column stacking (Body/Variables under Description)
//               • Description is one-line
//               • Close button handled by ModalWrapper (consistent UI)
//               • NO horizontal scrolling (force wrap / fixed table layout)
//
// Responsive pass (2026-09-16):
//               • Mobile header/badge stack cleanly; desktop presentation is retained.
//               • Language / Category / Description become one column on phones.
//               • Variable Mapping uses complete editable cards on mobile and the existing table at md+.
//               • Payload Preview / Variable Mapping flatten on mobile to avoid nested-card framing.
//               • Scan/Add/Cancel/Create actions become touch-friendly full-width controls on phones.
//               • No placeholder conversion, payload construction, validation or Twilio API behaviour changed.

import React, { useEffect, useMemo, useState } from "react";
import ModalWrapper from "./ModalWrapper";
import { useTheme } from "../context/ThemeContext.jsx";
import axiosInstance from "../api/axiosInstance";

const CATEGORY_OPTIONS = ["", "UTILITY", "MARKETING", "AUTHENTICATION"];

const BUILD_MARKER__ = "WhatsAppTemplateCreatorModal__2025-12-18__v1";

// ----------------------------
// Helpers
// ----------------------------
const isNumericPlaceholder = (key) => /^\d+$/.test(String(key || "").trim());

const toTitleWords = (s) =>
  String(s || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

const normalizeSnake = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_{2,}/g, "_");

const extractPlaceholders = (body) => {
  const text = String(body || "");
  // capture {{ ... }} but avoid nested braces
  const matches = text.match(/{{\s*([^}]+?)\s*}}/g) || [];
  const keys = [];
  const seen = new Set();
  matches.forEach((ph) => {
    const inner = ph.replace(/^{{\s*|\s*}}$/g, "").trim();
    if (!inner) return;
    const k = inner;
    if (seen.has(k)) return;
    seen.add(k);
    keys.push(k);
  });
  return keys;
};

const buildTwilioBodyAndVariables = (body, variablesSchema) => {
  // variablesSchema: [{ key, placeholder, label, source, sample_value }]
  // We will produce:
  //   twilioBody: body with placeholders rewritten to {{1}}, {{2}}...
  //   variablesMap: { "1": "Label", "2": "Label" }
  //   schemaWithNumbers: variables_schema but with key normalized to numeric strings + placeholder set to {{n}}
  const originalBody = String(body || "");
  const schema = Array.isArray(variablesSchema) ? variablesSchema : [];

  // Build ordered list of unique placeholder keys as they appear in schema
  // Prefer schema order; if empty, extract from body.
  const schemaKeys = schema.map((v) => String(v?.key || "").trim()).filter(Boolean);
  const fallbackKeys = extractPlaceholders(originalBody);
  const orderedKeys = schemaKeys.length ? schemaKeys : fallbackKeys;

  // If user already uses numeric placeholders only, keep as-is.
  const allNumeric = orderedKeys.length > 0 && orderedKeys.every((k) => isNumericPlaceholder(k));
  if (allNumeric) {
    const variablesMap = {};
    const schemaWithNumbers = schema.map((v) => {
      const n = String(v?.key || "").trim();
      variablesMap[n] = v?.label ? String(v.label) : `Var ${n}`;
      return {
        ...v,
        key: n,
        placeholder: `{{${n}}}`,
        label: v?.label ? String(v.label) : `Var ${n}`,
      };
    });

    return {
      twilioBody: originalBody,
      variablesMap,
      schemaWithNumbers,
      mappingPreview: orderedKeys.map((k) => ({ from: `{{${k}}}`, to: `{{${k}}}` })),
    };
  }

  // Otherwise assign numbers in order
  const keyToNum = new Map();
  let counter = 0;
  orderedKeys.forEach((k) => {
    const key = String(k || "").trim();
    if (!key) return;
    if (keyToNum.has(key)) return;
    counter += 1;
    keyToNum.set(key, String(counter));
  });

  // Rewrite body
  let rewritten = originalBody;
  // Replace exact placeholder occurrences (with whitespace tolerance)
  // We'll do a conservative replace for each key using regex: {{\s*KEY\s*}}
  keyToNum.forEach((num, key) => {
    const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`{{\\s*${escaped}\\s*}}`, "g");
    rewritten = rewritten.replace(re, `{{${num}}}`);
  });

  // Build variablesMap using labels from schema if possible; else title-case from key
  const variablesMap = {};
  keyToNum.forEach((num, key) => {
    const match = schema.find((v) => String(v?.key || "").trim() === key);
    const label = match?.label ? String(match.label) : toTitleWords(key);
    variablesMap[num] = label;
  });

  // Normalize schema keys to numeric keys
  const schemaWithNumbers = schema.length
    ? schema.map((v) => {
        const k = String(v?.key || "").trim();
        const num = keyToNum.get(k) || k;
        return {
          ...v,
          key: String(num),
          placeholder: `{{${num}}}`,
          label: v?.label ? String(v.label) : variablesMap[String(num)] || `Var ${num}`,
        };
      })
    : orderedKeys.map((k) => {
        const num = keyToNum.get(k);
        return {
          key: String(num),
          placeholder: `{{${num}}}`,
          label: variablesMap[String(num)] || toTitleWords(k),
          source: "",
          sample_value: "",
        };
      });

  const mappingPreview = orderedKeys.map((k) => {
    const num = keyToNum.get(k);
    return { from: `{{${k}}}`, to: `{{${num}}}` };
  });

  return {
    twilioBody: rewritten,
    variablesMap,
    schemaWithNumbers,
    mappingPreview,
  };
};

const WhatsAppTemplateCreatorModal = ({
  isOpen,
  open, // legacy
  onClose,
  tenantId = 1,
  onCreated,
}) => {
  const { theme } = useTheme();

  const visible = typeof isOpen === "boolean" ? isOpen : !!open;

  const [form, setForm] = useState({
    // internal/friendly name (Twilio friendly_name)
    name: "",
    description: "",
    language: "en_GB",
    category: "",
    bodyPreview: "",
  });

  const [variables, setVariables] = useState([]);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Hook order fix: keep hooks ABOVE any early return
  const accent = useMemo(() => theme?.accent_color || "var(--accent-color)" || "#ff7a00", [theme]);

  // ✅ Hook order fix: computedTwilio must be a hook, so it must run every render (even when hidden)
  const computedTwilio = useMemo(() => {
    const schema = Array.isArray(variables) ? variables : [];
    return buildTwilioBodyAndVariables(form.bodyPreview || "", schema);
  }, [form.bodyPreview, variables]);

  useEffect(() => {
    if (!visible) return;

    setError(null);
    setSaving(false);
    setScanning(false);
    setForm({
      name: "",
      description: "",
      language: "en_GB",
      category: "",
      bodyPreview: "",
    });
    setVariables([]);
  }, [visible]);

  // ✅ Now early return is safe (all hooks already called)
  if (!visible) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleVariableChange = (index, field, value) => {
    setVariables((prev) => {
      const clone = [...prev];
      clone[index] = { ...clone[index], [field]: value };
      return clone;
    });
  };

  const handleAddVariable = () => {
    setVariables((prev) => [
      ...prev,
      {
        key: `var_${prev.length + 1}`,
        placeholder: "{{ }}",
        label: "",
        source: "",
        sample_value: "",
      },
    ]);
  };

  const handleRemoveVariable = (index) => {
    setVariables((prev) => prev.filter((_, i) => i !== index));
  };

  const scanPlaceholders = () => {
    const body = form.bodyPreview || "";
    setScanning(true);
    setError(null);

    try {
      const keys = extractPlaceholders(body);
      const vars = keys.map((key) => ({
        key,
        placeholder: `{{${key}}}`,
        label: toTitleWords(key),
        source: "",
        sample_value: "",
      }));
      setVariables(vars);
    } catch (e) {
      console.error("[WhatsAppTemplateCreatorModal] scan error", e);
      setError("Failed to scan placeholders. Please check your body text.");
    } finally {
      setScanning(false);
    }
  };

  const handleSave = async () => {
    setError(null);

    const friendlyName = normalizeSnake(form.name);
    if (!friendlyName) {
      setError("Please provide an internal template name (e.g. pca_job_progress).");
      return;
    }
    if (!form.bodyPreview.trim()) {
      setError("Please provide a template body.");
      return;
    }
    if (!form.category) {
      setError("Please select a category (UTILITY / MARKETING / AUTHENTICATION).");
      return;
    }

    setSaving(true);
    try {
      // Convert placeholders to Twilio format ({{1}}, {{2}}...) + build variables map.
      const { twilioBody, variablesMap, schemaWithNumbers } = computedTwilio;

      const payload = {
        tenant_id: tenantId,
        friendly_name: friendlyName,
        language: form.language || "en_GB",
        category: form.category,
        content_type: "twilio/text",
        body: twilioBody,
        variables: variablesMap && Object.keys(variablesMap).length ? variablesMap : null,

        // local metadata enrichment
        internal_name: friendlyName,
        description: form.description || null,
        variables_schema:
          schemaWithNumbers && schemaWithNumbers.length
            ? schemaWithNumbers.map((v) => ({
                key: String(v.key || "").trim(),
                placeholder: String(v.placeholder || "").trim(),
                label: v.label ? String(v.label) : String(v.key || ""),
                source: v.source || null,
                sample_value: v.sample_value || null,
              }))
            : null,
      };

      console.log("[WhatsAppTemplateCreatorModal] BUILD", BUILD_MARKER__);
      console.log("[WhatsAppTemplateCreatorModal] create-in-twilio payload", payload);

      const resp = await axiosInstance.post("/messaging/templates/whatsapp/create-in-twilio", payload, {
        params: { tenant_id: tenantId },
      });

      const created = resp?.data;
      console.log("[WhatsAppTemplateCreatorModal] created", created);

      onCreated?.(created);
      onClose?.();
    } catch (e) {
      console.error("[WhatsAppTemplateCreatorModal] save error", e);
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        "Failed to create template in Twilio. Check backend logs.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = { display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 };

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: 10,
    border: "1px solid #cfcfcf",
    padding: "10px 12px",
    fontSize: 13,
    outline: "none",
    background: "#fff",
    minWidth: 0,
  };

  const thStyle = {
    textAlign: "left",
    padding: "8px 8px",
    borderBottom: "1px solid #e6e6e6",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const tdStyle = {
    padding: "8px 8px",
    borderBottom: "1px solid #f0f0f0",
    verticalAlign: "top",
    minWidth: 0,
  };

  return (
    <ModalWrapper onClose={onClose} size="full">
      {/* Header (no close button here — wrapper owns it) */}
      <div className="mb-[14px] flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center">
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 className="m-0 break-words text-xl font-semibold sm:text-2xl" style={{ color: accent }}>
            Create WhatsApp Template
          </h2>
          <p className="break-words" style={{ margin: "4px 0 0 0", fontSize: 13, color: "#666" }}>
            Create a Twilio Content template, submit for WhatsApp approval, then store the returned Content SID + status.
          </p>
          <div className="break-all" style={{ marginTop: 6, fontSize: 11, color: "#999", fontFamily: "monospace" }}>{BUILD_MARKER__}</div>
        </div>

        <span
          className="self-start sm:self-auto"
          style={{
            fontSize: 11,
            padding: "4px 10px",
            borderRadius: 999,
            border: `1px solid ${accent}`,
            color: accent,
            background: "rgba(255,122,0,0.05)",
            whiteSpace: "nowrap",
          }}
        >
          Twilio / WhatsApp
        </span>
      </div>

      {/* Form (single column stacking) */}
      <div className="grid min-w-0 grid-cols-1 gap-3 overflow-x-hidden sm:gap-[14px]">
        <div>
          <label style={labelStyle}>Internal Template Name (Twilio friendly_name)</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => handleChange("name", e.target.value)}
            style={{ ...inputStyle, fontFamily: "monospace" }}
            placeholder="e.g. pca_job_progress"
          />
          <div style={{ marginTop: 6, fontSize: 12, color: "#777" }}>
            Will be normalized to snake_case. Example: <span style={{ fontFamily: "monospace" }}>pca_job_progress</span>
          </div>
        </div>

        {/* Language / Category / Description row */}
        <div className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-12">
          <div className="min-w-0 md:col-span-3">
            <label style={labelStyle}>Language</label>
            <input
              type="text"
              value={form.language}
              onChange={(e) => handleChange("language", e.target.value)}
              style={inputStyle}
              placeholder="en_GB"
            />
          </div>

          <div className="min-w-0 md:col-span-3">
            <label style={labelStyle}>Category</label>
            <select
              value={form.category}
              onChange={(e) => handleChange("category", e.target.value)}
              style={{ ...inputStyle, paddingRight: 32 }}
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt || "__blank"} value={opt}>
                  {opt ? opt : "Select category…"}
                </option>
              ))}
            </select>
          </div>

          {/* Description (one line) */}
          <div className="min-w-0 md:col-span-6">
            <label style={labelStyle}>Description (internal)</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              style={inputStyle}
              placeholder="Internal notes about when and how this template is used."
            />
          </div>
        </div>

        {/* Body */}
        <div>
          <label style={labelStyle}>Body</label>
          <textarea
            value={form.bodyPreview}
            onChange={(e) => handleChange("bodyPreview", e.target.value)}
            rows={8}
            style={{ ...inputStyle, resize: "vertical" }}
            placeholder="Hello {{customer_forename}}, job {{job_ref}} update: {{progress_update}}"
          />

          <div className="mt-[10px] flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={scanPlaceholders}
              disabled={scanning || !form.bodyPreview.trim()}
              className="w-full rounded bg-btnActionNormal px-4 py-2 font-semibold text-white shadow hover:bg-btnActionHover active:bg-btnActionClick sm:w-auto"
              style={{
                opacity: scanning || !form.bodyPreview.trim() ? 0.55 : 1,
                cursor: scanning || !form.bodyPreview.trim() ? "default" : "pointer",
              }}
            >
              {scanning ? "Scanning…" : "Scan placeholders {{…}}"}
            </button>

            <div className="break-words" style={{ fontSize: 12, color: "#777", alignSelf: "center" }}>
              Use friendly keys like{" "}
              <span style={{ fontFamily: "monospace" }}>
                {"{{customer_forename}}"} {"{{job_ref}}"} {"{{progress_update}}"}
              </span>
              . We’ll convert to Twilio numeric placeholders automatically.
            </div>
          </div>
        </div>

        {/* Debug preview: what we will send to Twilio */}
        <div
          className="min-w-0 overflow-x-hidden border-t pt-3 sm:rounded-xl sm:border sm:border-dashed sm:p-3"
          style={{
            borderColor: accent,
            background: "transparent",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 800, color: accent, marginBottom: 8 }}>Twilio Payload Preview</div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Body (Twilio numeric placeholders)</div>
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: 12,
                  background: "#fff",
                  border: "1px solid #eee",
                  borderRadius: 10,
                  padding: 10,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {computedTwilio.twilioBody || "-"}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Variables map (number → label)</div>
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: 12,
                  background: "#fff",
                  border: "1px solid #eee",
                  borderRadius: 10,
                  padding: 10,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {computedTwilio.variablesMap && Object.keys(computedTwilio.variablesMap).length
                  ? JSON.stringify(computedTwilio.variablesMap, null, 2)
                  : "-"}
              </div>
            </div>

            {computedTwilio.mappingPreview?.length ? (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Placeholder conversion</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {computedTwilio.mappingPreview.map((m) => (
                    <span
                      key={`${m.from}-${m.to}`}
                      style={{
                        fontSize: 11,
                        padding: "4px 10px",
                        borderRadius: 999,
                        border: "1px solid #e6e6e6",
                        background: "#fff",
                        color: "#333",
                        fontFamily: "monospace",
                      }}
                      title={`${m.from} → ${m.to}`}
                    >
                      {m.from} → {m.to}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Variables */}
        <div className="min-w-0 overflow-x-hidden border-t pt-3 sm:rounded-xl sm:border sm:border-gray-200 sm:bg-[#fafafa] sm:p-3">
          <div className="mb-[10px] flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div style={{ fontSize: 14, fontWeight: 700 }}>Variable Mapping</div>
            <button
              type="button"
              onClick={handleAddVariable}
              className="w-full rounded bg-btnGeneralNormal px-3 py-2 font-semibold text-white hover:bg-btnGeneralHover active:bg-btnGeneralClick sm:w-auto"
              style={{ fontSize: 12 }}
            >
              + Add variable
            </button>
          </div>

          {variables.length === 0 ? (
            <div style={{ fontSize: 13, color: "#777" }}>
              No variables yet. Scan placeholders or add them manually.
            </div>
          ) : (
            <>
              {/* Mobile: one complete editable variable per card */}
              <div className="space-y-3 md:hidden">
                {variables.map((v, idx) => (
                  <div
                    key={`${v.key}-${idx}-mobile`}
                    className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
                  >
                    <div className="mb-3 flex min-w-0 items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-gray-800">
                          Variable {idx + 1}
                        </div>
                        <div className="mt-0.5 break-all font-mono text-[10px] text-gray-500">
                          {String(v.key || "").trim()
                            ? `{{${String(v.key || "").trim()}}}`
                            : "No placeholder key yet"}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveVariable(idx)}
                        aria-label={`Remove variable ${idx + 1}`}
                        title="Remove variable"
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-btnGeneralNormal font-black text-white hover:bg-btnGeneralHover active:bg-btnGeneralClick"
                      >
                        ×
                      </button>
                    </div>

                    <div className="grid min-w-0 grid-cols-1 gap-3">
                      <div className="min-w-0">
                        <label style={labelStyle}>Placeholder key</label>
                        <input
                          type="text"
                          value={v.key}
                          onChange={(e) =>
                            handleVariableChange(idx, "key", e.target.value)
                          }
                          style={{
                            ...inputStyle,
                            padding: "8px 10px",
                            fontSize: 12,
                            fontFamily: "monospace",
                          }}
                          placeholder="customer_forename"
                        />
                      </div>

                      <div className="min-w-0">
                        <label style={labelStyle}>Label</label>
                        <input
                          type="text"
                          value={v.label}
                          onChange={(e) =>
                            handleVariableChange(idx, "label", e.target.value)
                          }
                          style={{
                            ...inputStyle,
                            padding: "8px 10px",
                            fontSize: 12,
                          }}
                          placeholder="Customer Forename"
                        />
                      </div>

                      <div className="min-w-0">
                        <label style={labelStyle}>Source binding</label>
                        <input
                          type="text"
                          value={v.source}
                          onChange={(e) =>
                            handleVariableChange(idx, "source", e.target.value)
                          }
                          style={{
                            ...inputStyle,
                            padding: "8px 10px",
                            fontSize: 12,
                          }}
                          placeholder="contact.first_name / job.job_ref"
                        />
                      </div>

                      <div className="min-w-0">
                        <label style={labelStyle}>Sample value</label>
                        <input
                          type="text"
                          value={v.sample_value}
                          onChange={(e) =>
                            handleVariableChange(idx, "sample_value", e.target.value)
                          }
                          style={{
                            ...inputStyle,
                            padding: "8px 10px",
                            fontSize: 12,
                          }}
                          placeholder="e.g. Leon / 3059"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: retain the existing editable table */}
              <div className="hidden w-full overflow-x-hidden md:block">
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 12,
                    tableLayout: "fixed",
                  }}
                >
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: "23%" }}>Placeholder key</th>
                      <th style={{ ...thStyle, width: "19%" }}>Label</th>
                      <th style={{ ...thStyle, width: "29%" }}>Source binding</th>
                      <th style={{ ...thStyle, width: "23%" }}>Sample value</th>
                      <th style={{ ...thStyle, width: 44 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {variables.map((v, idx) => (
                      <tr key={`${v.key}-${idx}`}>
                        <td style={tdStyle}>
                          <input
                            type="text"
                            value={v.key}
                            onChange={(e) =>
                              handleVariableChange(idx, "key", e.target.value)
                            }
                            style={{
                              ...inputStyle,
                              padding: "8px 10px",
                              fontSize: 12,
                              fontFamily: "monospace",
                            }}
                            placeholder="customer_forename"
                          />
                          <div
                            style={{
                              marginTop: 6,
                              fontSize: 11,
                              color: "#888",
                              fontFamily: "monospace",
                            }}
                          >
                            Detected as: {"{{"}
                            {String(v.key || "").trim()}
                            {"}}"}
                          </div>
                        </td>

                        <td style={tdStyle}>
                          <input
                            type="text"
                            value={v.label}
                            onChange={(e) =>
                              handleVariableChange(idx, "label", e.target.value)
                            }
                            style={{
                              ...inputStyle,
                              padding: "8px 10px",
                              fontSize: 12,
                            }}
                            placeholder="Customer Forename"
                          />
                        </td>

                        <td style={tdStyle}>
                          <input
                            type="text"
                            value={v.source}
                            onChange={(e) =>
                              handleVariableChange(idx, "source", e.target.value)
                            }
                            style={{
                              ...inputStyle,
                              padding: "8px 10px",
                              fontSize: 12,
                            }}
                            placeholder="contact.first_name / job.job_ref"
                          />
                        </td>

                        <td style={tdStyle}>
                          <input
                            type="text"
                            value={v.sample_value}
                            onChange={(e) =>
                              handleVariableChange(idx, "sample_value", e.target.value)
                            }
                            style={{
                              ...inputStyle,
                              padding: "8px 10px",
                              fontSize: 12,
                            }}
                            placeholder="e.g. Leon / 3059"
                          />
                        </td>

                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          <button
                            type="button"
                            onClick={() => handleRemoveVariable(idx)}
                            aria-label="Remove variable"
                            title="Remove variable"
                            className="bg-btnGeneralNormal text-white hover:bg-btnGeneralHover active:bg-btnGeneralClick"
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: "50%",
                              fontWeight: 900,
                              lineHeight: "34px",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {error && (
          <div
            style={{
              padding: 10,
              borderRadius: 10,
              background: "#ffe5e5",
              color: "#a00",
              fontSize: 13,
              border: "1px solid #ffbdbd",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {error}
          </div>
        )}

        {/* Footer actions */}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-[10px]">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded bg-btnGeneralNormal px-4 py-2 font-semibold text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick sm:w-auto"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="w-full rounded bg-btnActionNormal px-4 py-2 font-semibold text-white shadow hover:bg-btnActionHover active:bg-btnActionClick sm:w-auto"
            style={{ opacity: saving ? 0.7 : 1, cursor: saving ? "default" : "pointer" }}
            title="Creates Content in Twilio + submits WhatsApp approval request, then saves record locally."
          >
            {saving ? "Creating…" : "Create in Twilio"}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default WhatsAppTemplateCreatorModal;
